import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios"; // Axios for API request
import Cookies from "js-cookie";
import { GET, POST, Select2Data } from "../../utils/api";
import { API } from "../../endpoints/common_endpoints";

export const getCurrentLocation = createAsyncThunk(
  "location/getCurrentLocation",
  async (_, { rejectWithValue, dispatch }) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        return reject(
          rejectWithValue("Geolocation is not supported by this browser.")
        );
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

          try {
            const response = await axios.get(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
            );

            const results = response?.data?.results;
             const addressDetails = response.data.results[0];
            if (
              !results ||
              response.data.status !== "OK" ||
              results.length === 0
            ) {
              return reject(
                rejectWithValue("Failed to fetch accurate location data")
              );
            }

            // Select best match
            const bestMatch =
              results.find(
                (r) =>
                  r.types.includes("street_address") ||
                  r.types.includes("premise") ||
                  r.types.includes("sublocality_level_1")
              ) || results[0];

            const addressComponents = bestMatch.address_components;

            const getComponent = (types) => {
              const match = addressComponents.find((comp) =>
                types.some((type) => comp.types.includes(type))
              );
              return match?.long_name || "";
            };

            const addressParts = {
              colony: getComponent(["neighborhood", "sublocality_level_2"]),
              area: getComponent(["sublocality_level_1", "sublocality"]),
              city: getComponent(["locality"]),
              state: getComponent(["administrative_area_level_1"]),
              country: getComponent(["country"]),
              pincode: getComponent(["postal_code"]),
            };

            const formattedCustomAddress = [
              addressParts.colony,
              addressParts.area,
              addressParts.city,
              addressParts.state,
              addressParts.country,
              addressParts.pincode,
            ]
              .filter(Boolean)
              .join(", ");

            const fullLocationData = {
              ...addressParts,
              formatted_address: formattedCustomAddress,
              raw: bestMatch,
              coordinates: { lat: latitude, lng: longitude },
              place_id: bestMatch.place_id,
              types: bestMatch.types,
            };

            // Save to cookies
            Cookies.set(
              "current_location_grocido",
              JSON.stringify(fullLocationData)
            );

            // Dispatch to Redux store
            dispatch(setCurrentLocation(addressDetails));

            resolve(fullLocationData);
          } catch (error) {
            console.error("Error fetching location from Google Maps:", error);
            reject(rejectWithValue("Google Maps API error"));
          }
        },
        (error) => {
          console.error("Geolocation error:", error);
          reject(rejectWithValue("Failed to get geolocation data"));
        }
      );
    });
  }
);

export const getCurrentLocationByLatLong = createAsyncThunk(
  "location/getCurrentLocationByLatLong",
  async (data, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            console.log(data, "latitude and longitude");
            try {
              // Fetch city and country based on latitude and longitude
              const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${data?.latitude}&lon=${data?.longitude}`
              );
              console.log("IP Geolocation data :----:", response?.data); // Log the response here
              Cookies.set(
                "current_location_grocido",
                JSON.stringify(response?.data)
              );
              resolve(response?.data); // Correctly resolve with the data
            } catch (error) {
              // Log the error message to the console
              console.error("Error fetching IP geolocation:", error);
              reject(
                rejectWithValue("Failed to fetch location data based on IP")
              );
            }
          },
          (error) => {
            console.error("Geolocation error:", error); // Log the error here
            reject(rejectWithValue("Failed to get geolocation data"));
          }
        );
      } else {
        reject(
          rejectWithValue("Geolocation is not supported by this browser.")
        );
      }
    });
  }
);

export const getLocationFromSession = createAsyncThunk(
  "getLocationFromSession",
  async (_, { rejectWithValue }) => {
    const current_location = Cookies.get("current_location_grocido");
    if (!current_location) {
      return rejectWithValue("No Location Found");
    }
    try {
      const response = JSON.parse(current_location);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Cuurent location get Faild");
    }
  }
);

// Thunk for getting all countries like name
export const getAllCountriesByName = createAsyncThunk(
  "master/getAllCountriesByName",
  async (name, { rejectWithValue }) => {
    try {
      const response = await POST(API.GET_ALL_COUNTRIES_BY_NAME, {
        name: name,
      });
      const country = response?.data?.data;
      return country;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getCityByName = createAsyncThunk(
  "master/getCityByName",
  async (name, { rejectWithValue }) => {
    try {
      const response = await POST(API.GET_CITY_BY_NAME, { name: name });
      const result = response?.data?.data;
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAllGooglePlaces = createAsyncThunk(
  "master/get-all-google-places",
  async ({ name }, { rejectWithValue }) => {
    try {
      const response = await GET(`${API.GET_ALL_GOOGLE_PLACES}?input=${name}`);
      const result = response;
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create the slice
const locationSlice = createSlice({
  name: "location",
  initialState: {
    error: null,
    locationError: null,
    status: "idle",
    current_location: {}, // The address is stored as an object
    selected_country: {},
    selected_city: {},
    google_location: {},
  },
  reducers: {
    setSelectedCountry: (state, action) => {
      state.selected_country = action.payload;
    },
    setSelectedCity: (state, action) => {
      // state.current_location = {};
      state.selected_city = action.payload;
    },
    setCurrentLocation: (state, action) => {
      // state.current_location = {};
      state.current_location = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentLocation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCurrentLocation.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.current_location = action.payload || {};
        // state.selected_city = {};
        console.log("Data in fulfilled reducer:", action.payload); // Logging for debugging
      })
      .addCase(getCurrentLocation.rejected, (state, action) => {
        state.status = "failed";
        state.locationError = action.payload;
        console.error("Error in rejected case:", action.payload); // Log the error for debugging
      })

      .addCase(getCurrentLocationByLatLong.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCurrentLocationByLatLong.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.current_location = action.payload || {};
        // state.selected_city = {};
        console.log("Data in fulfilled reducer:", action.payload); // Logging for debugging
      })
      .addCase(getCurrentLocationByLatLong.rejected, (state, action) => {
        state.status = "failed";
        state.locationError = action.payload;
        console.error("Error in rejected case:", action.payload); // Log the error for debugging
      })

      .addCase(getLocationFromSession.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getLocationFromSession.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.current_location = action.payload || {};
        console.log("Data in fulfilled reducer:", action.payload); // Logging for debugging
      })
      .addCase(getLocationFromSession.rejected, (state, action) => {
        state.status = "failed";
        state.locationError = action.payload;
        console.error("Error in rejected case:", action.payload); // Log the error for debugging
      })
      .addCase(getAllCountriesByName.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllCountriesByName.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selected_country = action.payload || {};
        console.log("Data in fulfilled reducer:", action.payload); // Logging for debugging
      })
      .addCase(getAllCountriesByName.rejected, (state, action) => {
        state.status = "failed";
        state.locationError = action.payload;
        console.error("Error in rejected case:", action.payload); // Log the error for debugging
      })
      .addCase(getCityByName.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCityByName.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selected_city = action.payload || {};
        console.log("Data in fulfilled reducer:", action.payload); // Logging for debugging
      })
      .addCase(getCityByName.rejected, (state, action) => {
        state.status = "failed";
        state.locationError = action.payload;
        console.error("Error in rejected case:", action.payload); // Log the error for debugging
      })

      .addCase(getAllGooglePlaces.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllGooglePlaces.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.google_location = action.payload || {};
        console.log("Data in fulfilled reducer:", action.payload); // Logging for debugging
      })
      .addCase(getAllGooglePlaces.rejected, (state, action) => {
        state.status = "failed";
        state.locationError = action.payload;
        console.error("Error in rejected case:", action.payload); // Log the error for debugging
      });
  },
});

export const {
  setSelectedCountry,
  setSelectedCity,
  setCurrentLocation,
} = locationSlice.actions;

export default locationSlice.reducer;
